import {useAuthenticationContext, useUserContext} from '@goschool/auth'
import {useEffect, useLayoutEffect, useRef} from 'react'
import {Outlet, Route, Routes, useLocation} from 'react-router-dom'
import {Routing} from '@goschool/routing'
import {InvitationPage} from '@goschool/invite'
import {
  AdminPage,
  AllChatsPage,
  CourseDocumentPage,
  CourseDocumentsPage,
  CoursePage,
  CoursesPage,
  CourseStudentsPage,
  CustomizeCourseAssistantPage,
  EditCoursePage,
  InvitationsPage,
  OrganizationsPage
} from '@goschool/courses'
import {CourseChatPage, CourseChatsPage, CourseChatViewPage} from '@goschool/chats'
import {SentryReportingErrorBoundary} from './SentryReportingErrorBoundary'
import {MSLQPage} from '@goschool/survey'
import {LoadingPage} from '@goschool/components'
import * as Sentry from '@sentry/react'

export interface GoSchoolRoutesProps {
  App: React.ComponentType,
  NotFound: React.ComponentType
}

export function GoSchoolRoutes({App, NotFound}: GoSchoolRoutesProps) {
  return <SentryReportingErrorBoundary>
    <Routes>
      <Route index={true} element={<App/>}/>
      <Route path="*" element={<NotFound/>}/>
      <Route element={<ProtectedRoute/>}>
        <Route path={Routing.admin}>
          <Route index={true} element={<AdminPage/>}/>
          <Route path={Routing.invitations} element={<InvitationsPage/>}/>
          <Route path={Routing.organizations} element={<OrganizationsPage/>}/>
          <Route path="chats" element={<AllChatsPage/>}/>
        </Route>
        <Route path={Routing.invitation} element={<InvitationPage/>}/>
        <Route path={Routing.courses} element={<CoursesPage/>}/>
        <Route path={Routing.course} element={<CoursePage/>}/>
        <Route path={Routing.documents} element={<CourseDocumentsPage/>}/>
        <Route path={Routing.courseEdit} element={<EditCoursePage/>}/>
        <Route path={Routing.courseCustomizeAssistant} element={<CustomizeCourseAssistantPage/>}/>
        <Route path={Routing.document} element={<CourseDocumentPage/>}/>
        <Route path={Routing.courseChat} element={<CourseChatPage/>}/>
        <Route path={Routing.courseChatView} element={<CourseChatViewPage/>}/>
        <Route path={Routing.courseStudents} element={<CourseStudentsPage/>}/>
        <Route path={Routing.courseChats} element={<CourseChatsPage/>}/>
        <Route path={Routing.surveyMslq} element={<MSLQPage/>}/>
      </Route>
    </Routes>
  </SentryReportingErrorBoundary>
}


function ProtectedRoute() {
  const {user, state} = useUserContext()
  const {signIn} = useAuthenticationContext()
  const location = useLocation()
  const stuckAuthLogged = useRef(false)

  useLayoutEffect(() => {
    if (user === null) {
      signIn()
    }
  }, [signIn, user])

  useEffect(() => {
    stuckAuthLogged.current = false
  }, [location.pathname, user, state])

  useEffect(() => {
    if (!location.pathname.startsWith('/invitations/')) {
      return
    }
    if (user !== null && user !== undefined) {
      return
    }
    const timeout = window.setTimeout(
      () => {
        if (stuckAuthLogged.current) {
          return
        }
        stuckAuthLogged.current = true
        Sentry.captureMessage('Invitation route stuck before authenticated user resolved', {
          level: 'error',
          extra: {
            href: window.location.href,
            pathname: location.pathname,
            userState: state
          }
        })
      },
      5000
    )
    return () => {
      window.clearTimeout(timeout)
    }
  }, [location.pathname, state, user])

  if (user == null || user === undefined) {
    return <LoadingPage/>
  }
  return <Outlet/>
}
